import { Icon } from "@material-ui/core";
import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
} from "common/interfaces";
import CalendarRange from "mdi-material-ui/CalendarRange";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Star from "mdi-material-ui/Star";
import Update from "mdi-material-ui/Update";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Header, Layout, PaneBody, PaneContent, SearchBar, SearchBarSort, SideBar } from "js/react/views/SharedResources/Components";
import PresentationTemplateList, {
    PresentationTemplateDragItem,
} from "js/react/views/SharedResources/Components/PresentationTemplateList";

import Banner from "js/Components/Banner";
import { Button as AppButton } from "js/Components/Button";
import { Icon as BaiIcon } from "js/Components/Icon";
import PresentationTemplatesPaneController, {
    PresentationTemplatesPaneControllerState,
} from "js/controllers/PresentationTemplatesPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getObjectHash from "common/utils/getObjectHash";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import { t } from "js/i18n";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { BlueButton } from "js/react/components/UiComponents";
import { selectedClass, SideBarItem } from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { useFolderDragItems } from "js/react/views/SharedResources/hooks/useFolderDragItems";
import { moveResourcesToFolder } from "js/react/views/SharedResources/utils/folderDropHelpers";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";

const SEARCH_FIELD_ALL = "__all__";
const TAG_FILTER_ALL = "__all__";

const contentFilterOptions = [
    { field: SEARCH_FIELD_ALL, label: t("All Content") },
    { field: "slide:text", label: t("Slide Text") },
    { field: "resource:metadata:name", label: t("Name") },
    { field: "resource:metadata:description", label: t("Description") },
    { field: "resource:tagsText", label: t("Tags") },
];

const sortOptions: SearchBarSort[] = [{
    field: "resource.metadata.name", label: t("Name"), direction: "asc", icon: <SortAlphabeticalVariant />,
}, {
    field: "resource.metadata.ratedByUsersCount", label: t("Rating"), direction: "desc", icon: <Star />,
}, {
    field: "resource.createdAt", label: t("Recently Created"), direction: "desc", icon: <CalendarRange />,
}, {
    field: "document.modifiedAt", label: t("Recently Modified"), direction: "desc", icon: <Update />,
}];

const ResultsContainer = styled.div`
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const StyledAppButton = styled(AppButton)`
    letter-spacing: 0.5px;
`;

export interface PresentationTemplatesPaneProps {
    editable?: boolean;
    onReady?: () => void;
    onSelect?: (presentationId: string | null, isDoubleClick?: boolean, selectedFolderId?: string) => void;
    onCreateFromTemplate?: (presentationId: string) => void;
    folderId?: string;
    onSelectFolder?: (pane: string, folderId?: string) => void;
}

type PresentationTemplatesPaneComponentProps =
    WorkspaceControllerState &
    PresentationTemplatesPaneControllerState &
    PresentationTemplatesPaneProps;

function PresentationTemplatesPane({
    editable = true,
    onSelect,
    onCreateFromTemplate,
    onReady,
    data,
    fetching,
    search,
    sort,
    tags,
    resourceFolders,
    folderId,
    onSelectFolder
}: PresentationTemplatesPaneComponentProps) {
    const [isInitializedForCurrentMount, setIsInitializedForCurrentMount] = useState(false);
    const [selectedTag, setSelectedTag] = useState(TAG_FILTER_ALL);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId ?? null);
    const [selectedPresentationId, setSelectedPresentationId] = useState<string | null>(null);
    const {
        draggedItems,
        handleStartDragItems,
        handleDropDragItems,
        clearDragItems,
        getActiveDragItems,
    } = useFolderDragItems(({ resourceId, resourceType }: PresentationTemplateDragItem) => ({
        resourceId,
        resourceType,
    }));

    useEffect(() => {
        setSelectedFolderId(folderId ?? null);
    }, [folderId]);

    const controllerFilters = (() => {
        const nextFilters = [];

        if (!editable) {
            nextFilters.push({
                field: "resource.metadata.isPublished",
                value: true,
                operator: "eq",
            });
        }

        if (selectedTag !== TAG_FILTER_ALL) {
            nextFilters.push({
                field: "resource.tags",
                operator: "eq",
                value: selectedTag,
            });
        }

        if (selectedFolderId) {
            nextFilters.push({
                field: "resource.folderIds",
                operator: "eq",
                value: selectedFolderId,
            });
        }

        return nextFilters;
    })();

    useEffect(() => {
        let isMounted = true;

        void PresentationTemplatesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            sort: [{
                field: "document.modifiedAt",
                direction: "desc",
            }],
            filters: controllerFilters,
        }).then(() => {
            if (isMounted) {
                setIsInitializedForCurrentMount(true);
            }
        });

        return () => {
            isMounted = false;
            PresentationTemplatesPaneController.reset();
        };
    }, []);

    useEffect(() => {
        if (!PresentationTemplatesPaneController.initialized) {
            return;
        }

        PresentationTemplatesPaneController.setFilters(controllerFilters);
    }, [getObjectHash(controllerFilters)]);

    const clearSearchQuery = () => {
        if (search.query) {
            PresentationTemplatesPaneController.setSearch({
                query: "",
                fields: search.fields,
            });
        }
    };

    const handleSearch = (searchTerm: string) => {
        PresentationTemplatesPaneController.setSearch({
            query: searchTerm?.trim(),
            fields: search.fields,
        });
    };

    const handleSort = (nextSort: SearchBarSort) => {
        PresentationTemplatesPaneController.setSort([{
            field: nextSort.field,
            direction: nextSort.direction,
        }]);
    };

    const handleContentFilterChange = (event: { target: { value: string } }) => {
        const searchField = event.target.value;
        PresentationTemplatesPaneController.setSearch({
            query: search.query,
            fields: searchField === SEARCH_FIELD_ALL ? undefined : [searchField],
        });
    };

    const handleTagFilterChange = (event: { target: { value: string } }) => {
        setSelectedTag(event.target.value);
    };

    const handleAddItem = () => {
        AppController.showCreatePresentationDialog({
            pane: CreatePresentationDialogPaneType.BLANK_PRESENTATION,
            isTeamTemplate: true,
            folderId: selectedFolderId,
        });
    };

    const handleFolderDrop = async (folder: IWorkspaceResourceFolder) => {
        const items = getActiveDragItems();
        if (!items.length) {
            return;
        }

        if (folder.id !== selectedFolderId) {
            setSelectedFolderId(folder.id);
            clearSearchQuery();
            onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES, folder.id);
        }

        clearDragItems();

        await moveResourcesToFolder({
            folder,
            resources: items,
            progressTitle: t("Moving template to folder..."),
            progressTitlePlural: count => t("Moving {{count}} templates to folder...", { count }),
            errorMessage: t("An error occurred while moving templates to the folder."),
            logTag: "PresentationTemplatesPane",
        });
    };

    const tagFilterOptions = [
        { field: TAG_FILTER_ALL, label: t("All Tags") },
        ...tags.map(({ tag, count }) => ({
            field: tag,
            label: `${tag} (${count})`,
        })),
    ];

    const selectedContentFilter = search.fields?.[0] ?? SEARCH_FIELD_ALL;
    const sortState: SearchBarSort = {
        field: sort[0]?.field ?? "document.modifiedAt",
        direction: sort[0]?.direction ?? "desc",
    };

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const showTopLevelEmptyState = !fetching &&
        data.length === 0 &&
        !search.query &&
        selectedTag === TAG_FILTER_ALL &&
        !selectedFolderId;
    const shouldRenderSideBar = isInitializedForCurrentMount && !(fetching && data.length === 0) && !showTopLevelEmptyState;

    const handleSelect = (presentationId: string | null, isDoubleClick?: boolean) => {
        if (!editable) {
            setSelectedPresentationId(presentationId);
        }
        onSelect?.(presentationId, isDoubleClick, selectedFolderId);
    };

    const canUpgradeToUseTeamTemplates = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.USE
    );

    const showCreateFromTemplateFooter = !editable &&
        !canUpgradeToUseTeamTemplates &&
        !!onCreateFromTemplate;

    return (
        <Layout>
            {onReady && <OnMountedReporter onMounted={onReady} />}

            {canUseSharedResources && (<>
                {editable && <Header title={t("Team Templates")}>
                    <StyledAppButton onClick={handleAddItem} blue large>
                        <BaiIcon white fill>add_circle</BaiIcon>
                        {t("New Template")}
                    </StyledAppButton>
                </Header>
                }

                {canUseSharedResources && (
                    <SearchBar
                        query={search.query ?? ""}
                        onSearch={handleSearch}
                        searchPlaceholder={t("Search team templates...")}
                        contentFilterOptions={contentFilterOptions}
                        selectedContentFilter={selectedContentFilter}
                        onContentFilterChange={handleContentFilterChange}
                        tagFilterOptions={tagFilterOptions}
                        selectedTagFilter={selectedTag}
                        onTagFilterChange={handleTagFilterChange}
                        sortOptions={sortOptions}
                        sort={sortState}
                        onSetSort={handleSort}
                        sortDisabled={(search.query ?? "").length > 0}
                    />
                )}
            </>)}

            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Team Presentation Templates")}
                        bodyText={t("Upgrade to a Team plan to give your team a head start with a library of presentation templates.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_templates" }
                            })}
                    />
                </FlexBox>
            )}

            <PaneBody>
                {shouldRenderSideBar && (
                    <SideBar
                        readOnly={!editable}
                        resourceTypes={[WorkspaceResourceType.PRESENTATION_TEMPLATE]}
                        folderType={WorkspaceResourceFolderType.SHARED}
                        createResourcesType={WorkspaceResourceType.PRESENTATION_TEMPLATE}
                        resourceFolders={resourceFolders}
                        selectedFolderId={selectedFolderId}
                        draggedItems={draggedItems}
                        onFolderDrop={handleFolderDrop}
                        onSelectFolder={(nextFolderId: string | null) => {
                            setSelectedFolderId(nextFolderId);
                            clearSearchQuery();
                            onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES, nextFolderId ?? undefined);
                        }}
                    >
                        <SideBarItem
                            className={selectedClass(selectedFolderId === null)}
                            id="all-templates"
                            onClick={() => {
                                setSelectedFolderId(null);
                                if (folderId) {
                                    onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES);
                                }
                            }}
                        >
                            <Icon className="material-symbols-outlined">select_all</Icon>
                            {t("All templates")}
                        </SideBarItem>
                    </SideBar>
                )}

                <PaneContent>
                    <ResultsContainer>
                        <PresentationTemplateList
                            editable={editable}
                            allowDragging={editable && canUseSharedResources}
                            selectedFolderId={selectedFolderId}
                            onSelect={handleSelect}
                            onStartDragItems={handleStartDragItems}
                            onDropDragItems={handleDropDragItems}
                            resourceFolders={resourceFolders}
                            onNavigateToFolder={({ folderId: nextFolderId, subFolderId }) => {
                                const nextSelectedFolderId = subFolderId ?? nextFolderId;
                                setSelectedFolderId(nextSelectedFolderId);
                                clearSearchQuery();
                                onSelectFolder?.(TeamResourceTypes.PRESENTATION_TEMPLATES, nextSelectedFolderId);
                            }}
                        />
                    </ResultsContainer>
                    {showCreateFromTemplateFooter && (
                        <FlexBox right fillWidth>
                            <BlueButton
                                id="create-presentation-btn"
                                onClick={() => {
                                    if (selectedPresentationId) {
                                        onCreateFromTemplate?.(selectedPresentationId);
                                    }
                                }}
                                disabled={!selectedPresentationId}
                            >
                                {t("Create Presentation From Template")}
                            </BlueButton>
                        </FlexBox>
                    )}
                </PaneContent>
            </PaneBody>
        </Layout>
    );
}

export default WorkspaceController.withInitializedState(
    PresentationTemplatesPaneController.withState(PresentationTemplatesPane)
);
